import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageShell } from "../components/Layout";
import api from "../lib/api";
import { ArrowRight, Calendar, CheckCircle, XCircle, Sun, Star, Clock, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import ToolAstrologerCta from "../components/ToolAstrologerCta";
import PaywallGate from "../components/PaywallGate";

const VERDICT_TINT = {
  excellent: { bg: "from-emerald-50 to-teal-100", border: "border-emerald-300", badge: "bg-emerald-300 text-emerald-950", label: "EXCELLENT" },
  very_good: { bg: "from-amber-50 to-yellow-100", border: "border-amber-300", badge: "bg-amber-300 text-amber-950", label: "VERY GOOD" },
  good: { bg: "from-indigo-50 to-violet-50", border: "border-indigo-300", badge: "bg-indigo-200 text-indigo-900", label: "GOOD" },
  neutral: { bg: "from-slate-50 to-slate-100", border: "border-slate-300", badge: "bg-slate-200 text-slate-800", label: "NEUTRAL" },
  avoid: { bg: "from-rose-50 to-orange-50", border: "border-rose-300", badge: "bg-rose-300 text-rose-950", label: "AVOID" },
};

function fmtDate(iso) {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-IN", { weekday: "long", year: "numeric", month: "short", day: "numeric" });
}

function todayIso(offset = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return d.toISOString().slice(0, 10);
}

function PickCard({ p }) {
  const tint = VERDICT_TINT[p.verdict] || VERDICT_TINT.good;
  return (
    <article className={`rounded-2xl overflow-hidden border ${tint.border}`} data-testid={`pick-${p.date}`}>
      <div className={`p-6 bg-gradient-to-br ${tint.bg}`}>
        <div className="flex items-baseline gap-3 flex-wrap">
          <CheckCircle size={18} weight="duotone" className="text-emerald-700" />
          <span className={`text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full ${tint.badge}`}>{tint.label}</span>
          <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full bg-white/70 border border-soft text-[#3F2D1B]">Score {p.score}</span>
          <h3 className="font-serif-display text-2xl font-light text-[#3F2D1B]">{fmtDate(p.date)}</h3>
        </div>
        <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-[#3F2D1B]/80">
          <span><b>Tithi:</b> {p.tithi_full}</span>
          <span><b>Nakshatra:</b> {p.nakshatra}</span>
          <span><b>Sunrise:</b> {p.sunrise || "—"}</span>
          <span><b>Sunset:</b> {p.sunset || "—"}</span>
        </div>
        {p.reasons_for?.length > 0 && (
          <ul className="mt-4 space-y-1.5 text-sm">
            {p.reasons_for.map((r, i) => (
              <li key={i} className="flex gap-2 leading-snug">
                <Star size={14} weight="duotone" className="text-emerald-700 flex-shrink-0 mt-1" />
                <span className="text-[#3F2D1B]">{r}</span>
              </li>
            ))}
          </ul>
        )}
        {p.reasons_against?.length > 0 && (
          <ul className="mt-3 space-y-1 text-xs">
            {p.reasons_against.map((r, i) => (
              <li key={i} className="flex gap-2 leading-snug text-rose-900/80">
                <Warning size={12} weight="duotone" className="flex-shrink-0 mt-0.5" />
                <span>{r}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      {p.best_windows?.length > 0 && (
        <div className="p-5 bg-white border-t border-soft">
          <div className="label-eyebrow text-emerald-900 flex items-center gap-2 mb-3"><Clock size={12} weight="duotone" /> Best time-windows on this day</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {p.best_windows.map((w, i) => (
              <div key={i} className="rounded-md p-2.5 bg-emerald-50 border border-emerald-200">
                <div className="text-[11px] font-medium text-emerald-900">{w.name}</div>
                <div className="text-xs text-[#3F2D1B] mt-0.5">{w.from} – {w.to}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      {p.avoid_windows?.length > 0 && (
        <div className="px-5 py-4 bg-rose-50/40 border-t border-rose-100">
          <div className="label-eyebrow text-rose-900 flex items-center gap-2 mb-2"><Warning size={12} weight="duotone" /> Avoid time-windows on this day</div>
          <div className="flex flex-wrap gap-2 text-xs">
            {p.avoid_windows.map((w, i) => (
              <span key={i} className="px-2 py-1 rounded bg-rose-100 text-rose-900 border border-rose-200">
                {w.name} · {w.from}–{w.to}
              </span>
            ))}
          </div>
        </div>
      )}
    </article>
  );
}

export default function MuhuratFinderPage() {
  const [activities, setActivities] = useState([]);
  const [form, setForm] = useState({
    activity: "gold_purchase",
    from_date: todayIso(0),
    to_date: todayIso(30),
    place: "",
    timezone: "Asia/Kolkata",
  });
  const [placeQuery, setPlaceQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/muhurat/activities").then((r) => setActivities(r.data?.activities || [])).catch(() => {});
  }, []);

  const submit = async (e) => {
    e?.preventDefault();
    const place = form.place || placeQuery;
    if (!form.activity || !form.from_date || !form.to_date || !place) {
      toast.error("Please fill activity, dates and place");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/muhurat/find", { ...form, place });
      setData(r.data);
      setTimeout(() => document.querySelector("[data-testid='muhurat-result']")?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to find muhurat");
    } finally {
      setLoading(false);
    }
  };

  const activityMeta = data?.activity;

  return (
    <PageShell>
      <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-10 pt-10 pb-20">
        <header className="mb-10">
          <div className="label-eyebrow text-muted flex items-center gap-2">
            <Calendar size={14} weight="duotone" /> Muhurat finder · Electional astrology
          </div>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-3 font-light leading-tight">
            Find the <span className="text-gold">perfect day & time</span>
          </h1>
          <p className="text-muted text-sm sm:text-base mt-4 font-light max-w-3xl">
            Pick an activity (health, finance, home buy/sell, gold buy/sell, family events, job, business launch, marriage, first meeting with a partner) and a date range. We score every day with tithi + vara + nakshatra + paksha + special muhurat yogas (<b>Guru Pushya, Ravi Pushya, Sarvartha Siddhi, Amrita Siddhi</b>), check Panchak and Bhadra blocks, and surface the best <b>Choghadiya / Abhijit</b> time-windows on each good day plus the <b>Rahu Kaal / Yamagandam / Gulika</b> windows to avoid.
          </p>
        </header>

        <form onSubmit={submit} className="rounded-2xl p-6 sm:p-7 bg-[#FAF4E8] border border-soft space-y-5 max-w-3xl" data-testid="muhurat-form">
          <label className="space-y-1 block">
            <span className="text-xs text-muted">Activity</span>
            <select
              value={form.activity}
              onChange={(e) => setForm({ ...form, activity: e.target.value })}
              className="w-full px-3 py-2.5 rounded-md border border-soft bg-white text-sm"
              data-testid="muhurat-activity"
            >
              {activities.map((a) => (
                <option key={a.key} value={a.key}>{a.label}</option>
              ))}
            </select>
            {activities.find((a) => a.key === form.activity)?.blurb && (
              <p className="text-[11px] text-muted mt-1 font-light italic">
                {activities.find((a) => a.key === form.activity).blurb}
              </p>
            )}
            {form.activity === "marriage" && (
              <Link to="/marriage-muhurat" className="inline-block text-[11px] text-gold underline mt-1" data-testid="muhurat-marriage-deep-link">
                Need a full wedding muhurat with both partners' charts? Open the Marriage Muhurat tool →
              </Link>
            )}
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="space-y-1">
              <span className="text-xs text-muted">From date</span>
              <input type="date" value={form.from_date} onChange={(e) => setForm({ ...form, from_date: e.target.value })} className="w-full px-3 py-2.5 rounded-md border border-soft bg-white" data-testid="muhurat-from" required />
            </label>
            <label className="space-y-1">
              <span className="text-xs text-muted">To date <span className="text-muted/70">(max 90 days)</span></span>
              <input type="date" value={form.to_date} onChange={(e) => setForm({ ...form, to_date: e.target.value })} className="w-full px-3 py-2.5 rounded-md border border-soft bg-white" data-testid="muhurat-to" required />
            </label>
          </div>
          <div className="relative">
            <label className="space-y-1 block">
              <span className="text-xs text-muted">Place (for sunrise/sunset and choghadiya)</span>
              <input
                type="text" value={placeQuery}
                onChange={async (e) => {
                  const q = e.target.value;
                  setPlaceQuery(q);
                  if (q.trim().length >= 2) {
                    try {
                      const { data: sug } = await api.get("/places/search", { params: { q: q.trim() } });
                      setSuggestions(sug || []);
                    } catch { /* ignore */ }
                  } else setSuggestions([]);
                }}
                placeholder="Start typing — Mumbai, Delhi, London..."
                className="w-full px-3 py-2.5 rounded-md border border-soft bg-white"
                data-testid="muhurat-place"
              />
            </label>
            {suggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 max-h-60 overflow-y-auto card-surface rounded-md border border-soft z-20 shadow-lg">
                {suggestions.map((s, i) => (
                  <button type="button" key={i}
                    onClick={() => {
                      const label = s.name || s.label;
                      setForm({ ...form, place: label, timezone: s.timezone || "Asia/Kolkata" });
                      setPlaceQuery(label); setSuggestions([]);
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-amber-50 text-sm border-b border-soft last:border-0"
                    data-testid={`muhurat-place-option-${i}`}
                  >{s.name || s.label}</button>
                ))}
              </div>
            )}
          </div>
          <button type="submit" disabled={loading} className="btn-primary rounded-full px-7 py-3 font-medium disabled:opacity-50" data-testid="muhurat-submit">
            {loading ? "Scanning days…" : <>Find my muhurat <ArrowRight size={16} className="inline ml-1" /></>}
          </button>
        </form>

        {data && (
          <div className="mt-12 space-y-8" data-testid="muhurat-result">
            {/* === HEADER === */}
            <section className="rounded-2xl p-6 bg-[#FAF4E8] border border-soft">
              <div className="label-eyebrow text-[#7C2D12]">{activityMeta.label}</div>
              <p className="text-sm text-muted mt-2 font-light">{activityMeta.blurb}</p>
              <div className="mt-4 text-xs text-muted">
                {fmtDate(data.from_date)} → {fmtDate(data.to_date)} · <b>{data.place_resolved}</b>
              </div>
              <div className="mt-4 flex flex-wrap gap-3 text-xs">
                <span className="px-2.5 py-1 rounded-full bg-emerald-200 text-emerald-950 font-medium">{data.summary.excellent} excellent</span>
                <span className="px-2.5 py-1 rounded-full bg-amber-200 text-amber-950 font-medium">{data.summary.very_good} very good</span>
                <span className="px-2.5 py-1 rounded-full bg-indigo-200 text-indigo-900 font-medium">{data.summary.good} good</span>
                <span className="px-2.5 py-1 rounded-full bg-slate-200 text-slate-900 font-medium">{data.summary.neutral} neutral</span>
                <span className="px-2.5 py-1 rounded-full bg-rose-200 text-rose-950 font-medium">{data.summary.avoid} avoid</span>
              </div>
            </section>

            {/* === DO/DON'T NOTES === */}
            <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-2xl p-5 bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200">
                <div className="label-eyebrow text-emerald-900 flex items-center gap-2 mb-3"><Sun size={12} weight="duotone" /> Classical do's</div>
                <ul className="space-y-2 text-sm">
                  {(activityMeta.do_notes || []).map((n, i) => (
                    <li key={i} className="flex gap-3 leading-snug">
                      <span className="text-emerald-700 mt-0.5">✦</span>
                      <span className="text-[#3F2D1B]">{n}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-2xl p-5 bg-gradient-to-br from-rose-50 to-orange-50 border border-rose-200">
                <div className="label-eyebrow text-rose-900 flex items-center gap-2 mb-3"><XCircle size={12} weight="duotone" /> Classical don'ts</div>
                <ul className="space-y-2 text-sm">
                  {(activityMeta.avoid_notes || []).map((n, i) => (
                    <li key={i} className="flex gap-3 leading-snug">
                      <span className="text-rose-700 mt-0.5">✦</span>
                      <span className="text-[#3F2D1B]">{n}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            {/* === TOP PICKS === */}
            <PaywallGate
              tool="muhurat"
              toolLabel="Muhurat · Optimal Date & Time"
              birthDetails={{
                date: form.from_date,
                time: form.to_date,
                place: `${form.activity}-${form.place || placeQuery}`,
                timezone: form.timezone,
              }}
              report={data}
            >
              <div className="space-y-8">
              {data.top_picks?.length > 0 && (
                <section className="space-y-5" data-testid="top-picks">
                  <div className="label-eyebrow text-emerald-900 flex items-center gap-2">
                    <CheckCircle size={14} weight="duotone" /> Top muhurats for {activityMeta.label.split("·")[0].trim()}
                  </div>
                  {data.top_picks.map((p) => <PickCard key={p.date} p={p} />)}
                </section>
              )}

              {/* === AVOID DAYS === */}
              {data.avoid_days?.length > 0 && (
                <section className="rounded-2xl p-6 bg-gradient-to-br from-rose-50 to-orange-50 border border-rose-200" data-testid="avoid-days">
                  <div className="label-eyebrow text-rose-900 flex items-center gap-2 mb-4">
                    <XCircle size={14} weight="duotone" /> Days to avoid in this window
                  </div>
                  <div className="space-y-3">
                    {data.avoid_days.map((p) => (
                      <div key={p.date} className="rounded-xl p-4 bg-white border border-rose-200" data-testid={`avoid-${p.date}`}>
                        <div className="flex items-baseline gap-3 flex-wrap">
                          <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full bg-rose-200 text-rose-950">AVOID</span>
                          <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">Score {p.score}</span>
                          <h4 className="font-serif-display text-lg font-light text-[#3F2D1B]">{fmtDate(p.date)}</h4>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-[#3F2D1B]/80">
                          <span><b>Tithi:</b> {p.tithi_full}</span>
                          <span><b>Nakshatra:</b> {p.nakshatra}</span>
                        </div>
                        <ul className="mt-2 space-y-1 text-xs">
                          {p.reasons_against.slice(0, 4).map((r, i) => (
                            <li key={i} className="flex gap-2 text-rose-900/90 leading-snug">
                              <Warning size={12} weight="duotone" className="flex-shrink-0 mt-0.5" />
                              <span>{r}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </section>
              )}
              </div>
            </PaywallGate>

            <ToolAstrologerCta
              requiredTechnique="Vedic"
              pitch={`Get a 1:1 muhurat consultation for your specific ${activityMeta.label.split("·")[0].trim().toLowerCase()} — with a Vedic specialist`}
              ctaLabel="Talk to"
            />
          </div>
        )}
      </div>
    </PageShell>
  );
}
